<template>
  <div class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md relative animate-scale-in overflow-hidden">
      <!-- Close Button -->
      <button @click="$emit('close')" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10">
        <span class="material-icons">close</span>
      </button>

      <!-- Tabs -->
      <div v-if="activeTab !== 'forgot-password'" class="flex border-b border-gray-100">
        <button 
          @click="activeTab = 'login'" 
          class="flex-1 py-4 text-sm font-bold uppercase tracking-wider transition-colors relative"
          :class="activeTab === 'login' ? 'text-primary' : 'text-gray-400 hover:text-gray-600'"
        >
          {{ $t('auth.login') }}
          <div v-if="activeTab === 'login'" class="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-t"></div>
        </button>
        <button 
          @click="activeTab = 'register'" 
          class="flex-1 py-4 text-sm font-bold uppercase tracking-wider transition-colors relative"
          :class="activeTab === 'register' ? 'text-primary' : 'text-gray-400 hover:text-gray-600'"
        >
          {{ $t('auth.register') }}
          <div v-if="activeTab === 'register'" class="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-t"></div>
        </button>
      </div>

      <div class="p-6 md:p-8">
        <!-- Password Recovery Success -->
        <div v-if="activeTab === 'forgot-password' && recoverySuccess" class="text-center space-y-4">
          <div class="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span class="material-icons text-4xl">check_circle</span>
          </div>
          <h3 class="text-xl font-bold text-gray-800">{{ $t('auth.recovery_email_sent') }}</h3>
          <button @click="activeTab = 'login'; recoverySuccess = false" class="text-primary font-bold hover:underline">
            {{ $t('auth.back_to_login') }}
          </button>
        </div>

        <!-- Login Form -->
        <form v-else-if="activeTab === 'login'" @submit.prevent="handleLogin" class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">{{ $t('auth.email') }}</label>
            <input type="email" v-model="loginForm.email" required class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
          </div>
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">{{ $t('auth.password') }}</label>
            <input type="password" v-model="loginForm.password" required class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
          </div>

          <div class="flex items-center justify-between text-xs">
            <label class="flex items-center text-gray-600 cursor-pointer">
              <input type="checkbox" class="mr-2 text-primary rounded focus:ring-primary"> {{ $t('auth.remember_me') }}
            </label>
            <button type="button" @click="activeTab = 'forgot-password'" class="text-primary hover:underline font-bold">{{ $t('auth.forgot_password') }}</button>
          </div>

          <button type="submit" :disabled="loading" class="w-full py-3 bg-primary text-white font-bold rounded-lg shadow-lg hover:bg-opacity-90 transition-all">
             {{ loading ? $t('auth.logging_in') : $t('auth.login') }}
          </button>
        </form>

        <!-- Register Form -->
        <form v-else-if="activeTab === 'register'" @submit.prevent="handleRegister" class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">{{ $t('auth.full_name') }}</label>
            <input type="text" v-model="registerForm.name" required class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
          </div>
           <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">{{ $t('auth.email') }}</label>
            <input type="email" v-model="registerForm.email" required class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
          </div>
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">{{ $t('auth.password') }}</label>
            <input type="password" v-model="registerForm.password" required class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
          </div>
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">{{ $t('auth.confirm_password') }}</label>
            <input type="password" v-model="registerForm.password_confirmation" required class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
          </div>

          <button type="submit" :disabled="loading" class="w-full py-3 bg-secondary text-white font-bold rounded-lg shadow-lg hover:bg-opacity-90 transition-all">
             {{ loading ? $t('auth.registering') : $t('auth.create_account') }}
          </button>
        </form>

        <!-- Forgot Password Form -->
        <form v-else-if="activeTab === 'forgot-password' && !recoverySuccess" @submit.prevent="handleForgotPassword" class="space-y-4">
          <div class="text-center mb-6">
            <h3 class="text-xl font-bold text-gray-800">{{ $t('auth.forgot_password_title') }}</h3>
            <p class="text-sm text-gray-500 mt-2">{{ $t('auth.forgot_password_desc') }}</p>
          </div>
          
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">{{ $t('auth.email') }}</label>
            <input type="email" v-model="recoveryForm.email" required class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
          </div>

          <button type="submit" :disabled="loading" class="w-full py-3 bg-primary text-white font-bold rounded-lg shadow-lg hover:bg-opacity-90 transition-all">
             {{ loading ? $t('auth.sending_instructions') : $t('auth.send_instructions') }}
          </button>

          <div class="text-center">
            <button type="button" @click="activeTab = 'login'" class="text-sm text-gray-500 hover:text-primary transition-colors">
              {{ $t('auth.back_to_login') }}
            </button>
          </div>
        </form>

        <div v-if="error" class="text-red-500 text-sm text-center mt-4 bg-red-50 p-2 rounded">
          {{ error }}
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import authService from '@/services/authService'

export default {
  name: 'AuthModal',
  data() {
    return {
      activeTab: 'login',
      loading: false,
      error: null,
      recoverySuccess: false,
      loginForm: {
        email: '',
        password: ''
      },
      registerForm: {
        name: '',
        email: '',
        password: '',
        password_confirmation: ''
      },
      recoveryForm: {
        email: ''
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['authLoading'])
  },
  watch: {
    // Lock scroll when modal is open (extra safety)
    activeTab: {
      immediate: true,
      handler() {
        document.body.style.overflow = 'hidden'
      }
    }
  },
  mounted() {
    document.body.style.overflow = 'hidden'
  },
  beforeDestroy() {
    document.body.style.overflow = ''
  },
  methods: {
    ...mapActions('auth', ['login']),
    
    async handleLogin() {
      this.loading = true
      this.error = null
      try {
        await this.login(this.loginForm)
        this.$emit('close')
      } catch (err) {
        this.error = err.response?.data?.message || this.$t('auth.login_error')
      } finally {
        this.loading = false
      }
    },

    async handleRegister() {
       this.loading = true
      this.error = null
      
      if (this.registerForm.password !== this.registerForm.password_confirmation) {
          this.error = this.$t('auth.password_mismatch')
          this.loading = false
          return
      }

      try {
        await authService.register(this.registerForm)
        await this.$store.dispatch('auth/checkSession')
        this.$emit('close')
      } catch (err) {
        this.error = err.response?.data?.message || this.$t('auth.register_error')
        if (err.response?.data?.errors) {
            this.error = Object.values(err.response.data.errors).flat().join(', ')
        }
      } finally {
        this.loading = false
      }
    },

    async handleForgotPassword() {
      this.loading = true
      this.error = null
      try {
        await authService.forgotPassword(this.recoveryForm.email)
        this.recoverySuccess = true
      } catch (err) {
        this.error = err.response?.data?.message || this.$t('auth.recovery_not_allowed')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

